import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import {
    Add as AddIcon,
    Visibility as ViewIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    CheckCircle as ActiveIcon,
    Cancel as InactiveIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import customerService from '../../../services/customerService';
import AppDataGrid from '../../common/AppDataGrid';
import GridActionsCell from '../../common/GridActionsCell';
import TableSearch from '../../common/TableSearch';
import ActiveFilter from '../../common/ActiveFilter';
import { buildToggleAction, useCanUpdate } from '../../common/activeToggle';
import { useDataTable } from '../../../hooks/useDataTable';

const CustomersList = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const canToggle = useCanUpdate('customers.update');
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    // Page, size, search, sort and the active filter live in the URL (/customers?page=0&size=20&...).
    const table = useDataTable(customerService.getCustomersPage, {}, {
        onError: () => toast.error(t('customers.fetchError')),
    });

    const handleToggleActive = async (customer) => {
        try {
            await customerService.toggleActive(customer.id);
            toast.success(customer.isActive === false ? t('common.activated') : t('common.deactivated'));
            table.refresh();
        } catch (error) {
            toast.error(error.response?.data?.message || t('common.toggleError'));
        }
    };

    const handleDeleteConfirm = async () => {
        try {
            await customerService.deleteCustomer(selectedCustomer.id);
            toast.success(t('customers.deleteSuccess'));
            setDeleteDialogOpen(false);
            setSelectedCustomer(null);
            table.refresh();
        } catch (error) {
            toast.error(error.response?.data?.message || t('common.deleteError'));
        }
    };

    const columns = [
        {
            // Sorts by first name server-side; the cell shows "first last" plus the website badge.
            field: 'firstName',
            headerName: t('common.name'),
            flex: 1,
            minWidth: 200,
            cellRenderer: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                    <Typography variant="body2" fontWeight="medium" noWrap>
                        {params.data.firstName} {params.data.lastName}
                    </Typography>
                    {params.data.source === 'WEBSITE' && (
                        <Chip label={t('customers.sourceWebsite')} size="small" color="primary" variant="outlined" />
                    )}
                </Box>
            ),
            tooltipValueGetter: (params) => `${params.data.firstName || ''} ${params.data.lastName || ''}`.trim() || null,
        },
        { field: 'email', headerName: t('customers.email'), width: 240, valueFormatter: (p) => p.value || '-' },
        { field: 'phone', headerName: t('customers.phone'), width: 160, valueFormatter: (p) => p.value || '-' },
        {
            field: 'emailVerifiedAt',
            headerName: t('customers.emailStatus'),
            width: 170,
            cellRenderer: (params) =>
                !params.data.email ? '-' : (
                    <Chip
                        label={params.value ? t('customers.emailConfirmed') : t('customers.emailNotConfirmed')}
                        color={params.value ? 'success' : 'warning'}
                        variant={params.value ? 'filled' : 'outlined'}
                        size="small"
                    />
                ),
            tooltipValueGetter: () => null,
        },
        {
            field: 'isActive',
            headerName: t('common.status'),
            width: 130,
            cellRenderer: (params) => (
                <Chip
                    icon={params.value !== false ? <ActiveIcon /> : <InactiveIcon />}
                    label={params.value !== false ? t('common.active') : t('common.inactive')}
                    color={params.value !== false ? 'success' : 'default'}
                    size="small"
                />
            ),
        },
        {
            field: 'actions',
            headerName: t('common.actions'),
            width: 150,
            sortable: false,
            cellRenderer: (params) => (
                <GridActionsCell
                    actions={[
                        {
                            key: 'view',
                            icon: ViewIcon,
                            title: t('common.view'),
                            color: 'info',
                            onClick: () => navigate(`/customers/${params.data.id}`),
                        },
                        {
                            key: 'edit',
                            icon: EditIcon,
                            title: t('common.edit'),
                            color: 'primary',
                            onClick: () => navigate(`/customers/edit/${params.data.id}`),
                        },
                        buildToggleAction({ row: params.data, onToggle: handleToggleActive, t, canToggle }),
                        {
                            key: 'delete',
                            icon: DeleteIcon,
                            title: t('common.delete'),
                            color: 'error',
                            onClick: () => {
                                setSelectedCustomer(params.data);
                                setDeleteDialogOpen(true);
                            },
                        },
                    ]}
                />
            ),
        },
    ];

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">{t('customers.title')}</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/customers/create')}
                >
                    {t('customers.createCustomer')}
                </Button>
            </Box>

            <Card>
                <CardContent>
                    <TableSearch
                        placeholder={t('customers.searchPlaceholder')}
                        value={table.searchInput}
                        onChange={table.handleSearchChange}
                        sx={{ mb: 2 }}
                    />

                    <ActiveFilter
                        value={table.params.active || 'all'}
                        onChange={(active) => table.updateParams({ active })}
                        sx={{ mb: 2 }}
                    />

                    <AppDataGrid
                        rows={table.data.content}
                        columns={columns}
                        loading={table.loading}
                        paginationMode="server"
                        rowCount={table.data.totalElements}
                        paginationModel={table.paginationModel}
                        onPaginationModelChange={table.handlePaginationModelChange}
                        sortModel={table.sortModel}
                        onSortModelChange={table.handleSortChange}
                        pageSizeOptions={[10, 20, 50, 100]}
                        getRowId={(row) => row.id}
                    />
                </CardContent>
            </Card>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>{t('customers.deleteCustomer')}</DialogTitle>
                <DialogContent>
                    <Typography>
                        {t('common.deleteConfirmText', { name: `${selectedCustomer?.firstName} ${selectedCustomer?.lastName}` })}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        {t('common.delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CustomersList;
